import type { RepoGraderInput, RepoGraderResult, ChecklistItem, RiskFlag } from "../../types";
import { uid } from "../storage";

const PAYWALL_PATTERNS = [
  /openai/i,
  /anthropic/i,
  /gemini/i,
  /openrouter/i,
  /replicate/i,
  /midjourney/i,
  /API_KEY/i,
  /sk-[a-zA-Z0-9]{10,}/,
];

const STRUCTURE_HINTS = [
  { pattern: /package\.json/i, label: "package.json manifest" },
  { pattern: /wrangler\.(toml|jsonc)/i, label: "Cloudflare wrangler config" },
  { pattern: /vite\.config/i, label: "Vite config" },
  { pattern: /next\.config/i, label: "Next.js config" },
  { pattern: /src\//i, label: "src/ directory reference" },
  { pattern: /README/i, label: "README documentation" },
  { pattern: /tsconfig/i, label: "TypeScript config" },
  { pattern: /tailwind/i, label: "Tailwind styling" },
];

export function gradeRepo(input: RepoGraderInput): RepoGraderResult {
  const text = input.pathOrPaste.trim();
  const lines = text.split(/\r?\n/).filter(Boolean);
  const lower = text.toLowerCase();

  const checklist: ChecklistItem[] = STRUCTURE_HINTS.map((hint, index) => {
    const passed = hint.pattern.test(text);
    return {
      id: `chk_${index}`,
      label: hint.label,
      passed,
      detail: passed ? "Detected in input." : "Not found in pasted content or path notes.",
    };
  });

  checklist.push({
    id: "chk_entry",
    label: "Entry point hints",
    passed: /main\.(tsx|ts|jsx|js)|index\.html|app\/page\.tsx/i.test(text),
    detail: "Looks for common application entry references.",
  });

  const riskFlags: RiskFlag[] = [];

  for (const pattern of PAYWALL_PATTERNS) {
    if (pattern.test(text)) {
      riskFlags.push({
        id: uid("risk"),
        severity: pattern.source.includes("KEY") || pattern.source.includes("sk-") ? "high" : "medium",
        category: "paywall-or-api",
        message: `Pattern matched: ${pattern.source}`,
      });
    }
  }

  if (/eval\s*\(/i.test(text)) {
    riskFlags.push({
      id: uid("risk"),
      severity: "high",
      category: "unsafe-execution",
      message: "eval() usage detected in pasted content.",
    });
  }

  if (/child_process|exec\s*\(/i.test(text)) {
    riskFlags.push({
      id: uid("risk"),
      severity: "medium",
      category: "shell-execution",
      message: "Shell/process execution references detected.",
    });
  }

  if (lines.length < 3 && !text.includes("/")) {
    riskFlags.push({
      id: uid("risk"),
      severity: "low",
      category: "sparse-input",
      message: "Input is very short — paste README, package.json, or tree listing for better grading.",
    });
  }

  const passedCount = checklist.filter((c) => c.passed).length;
  const salvageBase = (passedCount / checklist.length) * 70;
  const riskPenalty = riskFlags.filter((r) => r.severity === "high").length * 15
    + riskFlags.filter((r) => r.severity === "medium").length * 7;
  const lovableBonus = lower.includes("lovable") ? 5 : 0;
  const salvageScore = Math.max(
    0,
    Math.min(100, Math.round(salvageBase - riskPenalty + lovableBonus)),
  );

  let suggestedBuildPath = "Scaffold a Vite + React sovereign surface with local deterministic engines.";
  if (lower.includes("next")) suggestedBuildPath = "Extend as Next.js app with static export or CF adapter; strip server-only paid deps.";
  if (lower.includes("worker") || lower.includes("wrangler")) suggestedBuildPath = "Deploy as Cloudflare Worker/Pages with wrangler.toml and local-first UI.";
  if (salvageScore >= 70) suggestedBuildPath = "Salvage existing structure — merge stronger logic into current deploy base.";
  if (salvageScore < 30) suggestedBuildPath = "Treat as greenfield — use BOB intake → artifact planner → handoff export pipeline.";

  return {
    id: uid("grade"),
    scannedAt: new Date().toISOString(),
    checklist,
    riskFlags,
    salvageScore,
    suggestedBuildPath,
    summary: `Static audit complete. ${passedCount}/${checklist.length} checklist items passed. Salvage score ${salvageScore}/100.`,
  };
}
